import { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';

import { SecondaryButton } from 'ui/button';
import { TextArea } from 'ui/inputs/textArea';
import { InfoBanner } from 'ui/banner';
import { Confirm } from 'ui/icons/action/Confirm';
import { MakeCopy } from 'ui/icons/action/MakeCopy';
import { caption, colors, monospace2, spacing } from 'ui/theme';

import { UserActionsMetadata } from 'modules/campaigns/lib/components';
import { GraphQLSnippet } from '../types';
import { CopyToClipboard } from './CopyToClipboard';
import {
  SnippetDetailsContainer,
  SnippetInfo,
  SnippetMetaData,
  SnippetName,
  SnippetSectionContainer,
} from './Styled';

import { generateSlug } from '../utils';

type Props = {
  snippet: GraphQLSnippet;
};

const ContentContainer = styled.div`
  ${monospace2}
  background: ${colors.white};
  border-radius: ${spacing.half};
  border: ${spacing.single} dashed ${colors.grey25};
  color: ${colors.cyan100};
  display: flex;
  padding: ${spacing.plusQuarter};
  position: relative;
  cursor: pointer;
`;

const SnippetTextAndButtonContainer = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  gap: ${spacing.threeQuarter};
`;

const SnippetContainer = styled.div`
  flex: 1;
`;

const SecondaryCaption = styled.div`
  ${caption}
  color: ${colors.textSecondary};
`;

export const SnippetDetails = ({ snippet }: Props) => {
  const [isCopied, setIsCopied] = useState(false);

  const copiedTimer = useRef<ReturnType<typeof setTimeout>>();

  // Show 'copied' indicator, then revert after 3 seconds
  const onCopy = useCallback(() => {
    if (copiedTimer.current) {
      clearTimeout(copiedTimer.current);
    }
    setIsCopied(true);
    copiedTimer.current = setTimeout(() => {
      setIsCopied(false);
    }, 3000);
  }, []);

  const snippetCodePreview = generateSlug(snippet.name, snippet.positionalParameters);

  return (
    <SnippetDetailsContainer>
      <InfoBanner>
        All HTML tags will get <b>escaped</b>. To ensure your message displays correctly make sure
        you are only using plaintext snippets
      </InfoBanner>

      <SnippetInfo>
        <SnippetName data-test="snippet-content">{snippet.name}</SnippetName>
        <SnippetMetaData>
          <SecondaryCaption data-test="snippet-detail-key">ID: {snippet.id}</SecondaryCaption>
          <SecondaryCaption data-test="snippet-detail-key">{snippet.description}</SecondaryCaption>
        </SnippetMetaData>
      </SnippetInfo>

      <SnippetSectionContainer>
        <CopyToClipboard text={snippetCodePreview} onCopy={onCopy}>
          <ContentContainer>
            <SnippetTextAndButtonContainer>
              <SnippetContainer>{snippetCodePreview}</SnippetContainer>

              <SecondaryButton data-test="copy-to-clipboard-button">
                {isCopied ? (
                  <Confirm isColorInherited={true} />
                ) : (
                  <MakeCopy width={16} height={16} />
                )}
                <div>{isCopied ? 'Copied' : 'Copy'}</div>
              </SecondaryButton>
            </SnippetTextAndButtonContainer>
          </ContentContainer>
        </CopyToClipboard>
      </SnippetSectionContainer>

      <SnippetSectionContainer>
        <TextArea isDisabled={true} value={snippet.content} resize="vertical" />
      </SnippetSectionContainer>

      <SnippetSectionContainer>
        <UserActionsMetadata
          creator={snippet?.creatorUser}
          modifier={snippet?.updatedByUser}
          createdDate={snippet?.createdAt}
          modifiedDate={snippet?.updatedAt}
          backgroundColor={`${colors.grey5}`}
        />
      </SnippetSectionContainer>
    </SnippetDetailsContainer>
  );
};
